/**
 * Day shift / night mail.
 *
 * The chosen mode is stamped on <html> as `data-theme`, which flips
 * `color-scheme` and with it every `light-dark()` token (styles/theme/tokens.css)
 * — and the browser's own widgets: date pickers, scrollbars, autofill.
 *
 * No stored choice means "follow the OS", which is the default: nothing is
 * stamped and `color-scheme: light dark` lets the system decide. Applied from
 * main.tsx before React renders, so a night user never sees a white flash.
 */

export type ThemeMode = "day" | "night";

const STORAGE_KEY = "letterman.theme";

function isMode(value: string | null): value is ThemeMode {
  return value === "day" || value === "night";
}

/** The stored choice, or null when the OS decides. */
export function storedTheme(): ThemeMode | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isMode(raw) ? raw : null;
  } catch {
    // Private mode, or site data blocked: fall back to the OS.
    return null;
  }
}

/** What is actually on screen right now, stored choice or not. */
export function resolvedTheme(): ThemeMode {
  const stored = storedTheme();
  if (stored) return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "night" : "day";
}

const THEME_EVENT = "letterman:theme";

/** Stamp a choice (or clear it, handing the decision back to the OS). */
export function applyTheme(mode: ThemeMode | null): void {
  const root = document.documentElement;
  if (mode) root.dataset.theme = mode;
  else delete root.dataset.theme;
  try {
    if (mode) localStorage.setItem(STORAGE_KEY, mode);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // The stamp already applied; only persistence is lost.
  }
  window.dispatchEvent(new Event(THEME_EVENT));
}

/**
 * Notify on every change of the mode on screen — a choice made here, or the
 * OS flipping under a viewer who has made no choice.
 *
 * Tokens follow `color-scheme` on their own; this is for the components that
 * cannot, because they carry their own palette: CodeMirror ships a light
 * theme and has to be handed the other one.
 */
export function subscribeTheme(onChange: () => void): () => void {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  window.addEventListener(THEME_EVENT, onChange);
  media.addEventListener("change", onChange);
  return () => {
    window.removeEventListener(THEME_EVENT, onChange);
    media.removeEventListener("change", onChange);
  };
}

/** Re-apply the stored choice. Call once, before the first render. */
export function initTheme(): void {
  const stored = storedTheme();
  if (stored) document.documentElement.dataset.theme = stored;
}
